import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatPrice, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function OrderTracker({ onClosed }) {
  const { t, lang } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [closed, setClosed] = useState(false);
  const unpaidSeen = useRef(null);

  const load = useCallback(() => {
    api.get("/orders/mine").then((r) => {
      const list = r.data;
      const unpaidNow = new Set(list.filter((o) => o.payment_status !== "paid").map((o) => o.order_id));
      if (unpaidSeen.current) {
        const justPaid = [...unpaidSeen.current].some((id) => !unpaidNow.has(id) && list.some((o) => o.order_id === id && o.payment_status === "paid"));
        if (justPaid) setClosed(true);
      }
      unpaidSeen.current = unpaidNow;
      setOrders(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!closed) return;
    const id = setTimeout(() => {
      setClosed(false);
      onClosed?.();
    }, 3000);
    return () => clearTimeout(id);
  }, [closed, onClosed]);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (o) => {
    try {
      await api.delete(`/orders/${o.order_id}`);
      toast.success(t("order_cancelled"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (closed) {
    return (
      <div data-testid="order-closed-banner" className="max-w-2xl bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-6 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <p className="font-heading font-bold text-xl text-emerald-800">{t("order_closed_paid")}</p>
        <p className="text-sm text-emerald-700 mt-1">{t("order_closed_desc")}</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return <p className="text-[#786C66] text-sm" data-testid="no-orders">{t("no_orders")}</p>;
  }

  return (
    <div className="space-y-3 max-w-2xl" data-testid="order-tracker">
      {orders.map((o) => (
        <div key={o.order_id} data-testid={`order-${o.order_id}`} className="bg-white border border-[#E8DFD5] rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="font-heading font-semibold text-[#1F1615]">
                {t("table")} {o.table_number} · {formatPrice(o.total)}
              </p>
              <p className="text-xs text-[#786C66]">{new Date(o.created_at).toLocaleString(lang === "it" ? "it-IT" : "en-GB")}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={o.status} />
              <PaymentBadge paid={o.payment_status === "paid"} />
            </div>
          </div>
          <p className="text-sm text-[#786C66] mt-2">
            {o.items.map((i) => `${i.quantity}× ${lang === "it" ? i.name_it : i.name_en}`).join(", ")}
          </p>
          {(o.people || 0) > 0 && (
            <p className="text-xs text-[#786C66] mt-1" data-testid={`order-cover-${o.order_id}`}>
              {o.people} {t("people")} · {t("cover")} {formatPrice(o.cover_total || 0)}
            </p>
          )}
          {o.note && <p className="text-xs italic text-[#786C66] mt-1">“{o.note}”</p>}
          {o.status === "pending" && (
            <Button
              data-testid={`cancel-order-${o.order_id}`}
              size="sm"
              variant="outline"
              onClick={() => cancel(o)}
              className="mt-3 rounded-full border-red-200 text-red-600 hover:bg-red-50 text-xs h-8 gap-1"
            >
              <XCircle className="w-3.5 h-3.5" /> {t("cancel_order")}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
